"use client";
import Image from "next/image";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Elevation from "./icons/elevation";
import TelephoneAreaCode from "./icons/telephone-area-code";
import LatitudeLongitude from "./icons/latitude-longitude";
import TimeZone from "./icons/time-zone";

const fadeTop = {
  hidden: { opacity: 0, y: -100 },
  visible: { opacity: 1, y: 0, transition: { duration: 1 } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { duration: 1 } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 1 } },
};

const tabKeys = ["overView", "demography", "PhotosGallery", "founder"];

const icons = [
  <Elevation className="w-16 lg:w-[6vw] 2xl:w-[5vw] h-auto p-3 border-2 border-green-4 rounded-md" />,
  <TelephoneAreaCode className="w-16 lg:w-[6vw] 2xl:w-[5vw] h-auto p-3 border-2 border-green-4 rounded-md" />,
  <LatitudeLongitude className="w-16 lg:w-[6vw] 2xl:w-[5vw] h-auto p-3 border-2 border-green-4 rounded-md" />,
  <TimeZone className="w-16 lg:w-[6vw] 2xl:w-[5vw] h-auto p-3 border-2 border-green-4 rounded-md" />
]

const Tabs = ({ tabs, backgroundImage }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const contentKey = tabKeys[activeIndex];
  const content = tabs[contentKey] || {};
  const isGalleryTab = contentKey === "PhotosGallery";
  const leftWidth = isGalleryTab ? "lg:w-[60%]" : "lg:w-1/2";
  const rightWidth = isGalleryTab
    ? "w-full lg:w-[65%] 2xl:w-[60%]"
    : "w-full lg:w-1/2";

  return (
    <>
      {/* Tab Navigation */}
      <div
        className="lg:ml-6 relative w-full rounded-lg flex lg:w-[80%] xl:w-[75%] 2xl-w-[60%] gap-3 2xl:gap-12 justify-start 2xl:justify-normal mb-3 pb-2 lg:mb-5 overflow-x-visible overflow-y-hidden"
        role="tablist"
        aria-label="Content Tabs"
      >
        {tabs.aboutLinks.map((tab, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={activeIndex === i}
            aria-controls={`tabpanel-${i}`}
            id={`tab-${i}`}
            onClick={() => setActiveIndex(i)}
            className={`px-4 py-1.5 text-base 2xl:text-lg font-medium rounded-md transition-all duration-300 text-nowrap ${activeIndex === i
              ? "bg-green-11 text-white shadow-md px-8"
              : "bg-[#EDF0FF] text-green-11 hover:bg-green-9 hover:text-white hover:shadow-md"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeIndex}
          role="tabpanel"
          id={`tabpanel-${activeIndex}`}
          aria-labelledby={`tab-${activeIndex}`}
          className={`${contentKey === "PhotosGallery" || contentKey === "founder"
            ? "gap-x-[2%]"
            : "justify-between gap-x-[5%]"
            } lg:ml-6 relative mb:px-3 w-full lg:flex  rounded-md bg-no-repeat bg-left-bottom font-extralight`}
          style={
            backgroundImage
              ? { backgroundImage: `url('${backgroundImage}')` }
              : {}
          }
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
          exit={{ opacity: 0, y: -20, transition: { duration: 0.4 } }}
        >
          {/* Left Content */}
          <div className={leftWidth}>
            {content.description && (
              <motion.div
                variants={fadeLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className=" text-base font-normal leading-[165%] text-justify"
              >
                {contentKey !== "founder" &&
                  (Array.isArray(content.description) ? (
                    content.description.map((paragraph, i) => (
                      <p key={i} className="mb-4 lg:text-lg lg:font-semibold">
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <>
                      {content?.title && (
                        <h3 className="font-bold mb-2 text-xl text-green-11">
                          {content.title}
                        </h3>
                      )}
                      <p
                        className="tracking-[1px] leading-[165%] lg:text-lg lg:font-semibold"
                        dangerouslySetInnerHTML={{
                          __html: content.description,
                        }}
                      />
                    </>
                  ))}
              </motion.div>
            )}
            {/* Demography tab small GIF images */}
            {contentKey === "demography" && content.images && (
              <section className="w-full grid grid-cols-2  gap-8">
                {content.images.map((image, index) => (
                  <div
                    key={`small-${index}`}
                    className="flex flex-col items-center text-center p-4 border-2 border-green-12 rounded-xl shadow-sm hover:shadow-md transition"
                  >
                    {icons?.[index]}

                    <h3 className="mt-3 text-base font-semibold text-green-2">
                      {image.title}
                    </h3>
                    <p className="mt-1 text-sm md:text-base text-gray-600 font-semibold">{image.desc}</p>
                  </div>
                ))}
              </section>

            )}
            {contentKey === "founder" && (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_3fr]">
                <div>
                  <Image
                    className="w-32 md:w-40 lg:w-[10vw] h-auto border-2 border-green-4 bg-yellow-50 rounded-md"
                    {...content.imagesCircle.image2}
                    width={200}
                    height={50}
                    alt="founder-2"
                    sizes="(max-width: 768px) 128px, (max-width: 1280px) 10vw, 10vw"
                  />
                  {/* <p className="text-[10px] leading-[165%] xl:text-xs font-semibold text-green-9 text-nowrap">
                    {content.imagesCircle?.image2?.alt}
                  </p> */}
                </div>
                <p
                  className="font-normal leading-[165%] text-justify"
                  dangerouslySetInnerHTML={{ __html: content.description }}
                />
              </div>
            )}
          </div>

          {/* Right Content */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={rightWidth}
          >
            {/* Overview Tab - Google Map */}
            {contentKey === "overView" && content.images?.image1 && (
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3891.4270367689905!2d78.3370103758851!3d12.750756219680056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3badb9cad0091edb%3A0xfc9819e46cbd4715!2sRESCO!5e0!3m2!1sen!2sin!4v1743430926616!5m2!1sen!2sin"
                className="border border-green-4 shadow-sm shadow-green-1 mt-4 lg:mt-0 w-full lg:w-[80%] rounded-xl h-[350px]"
                allowFullScreen=""
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>

            )}

            {/* Demography Tab - Circle Map */}
            {contentKey === "demography" && content.imagesCircle?.image1 && (

              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15438.10455493861!2d78.3265113!3d12.7498387!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3badb9cc1ee13607%3A0x291c3b4ae41acabf!2sKuppam%2C%20Andhra%20Pradesh!5e0!3m2!1sen!2sin!4v1694524800000!5m2!1sen!2sin"
                className="border border-green-4 shadow-sm shadow-green-1 mt-4 lg:mt-0 w-full lg:w-[80%] h-full  rounded-xl"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

            )}

            {contentKey === "founder" && content.imagesCircle?.image1 && (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_3fr] mt-12 lg:mt-0">
                <div>
                  <Image
                    className="w-32 md:w-40 lg:w-[10vw] h-auto border-2 border-green-4 bg-yellow-50 rounded-md"
                    {...content.imagesCircle.image1}
                    width={200}
                    height={50}
                    alt="founder-1"
                    sizes="(max-width: 768px) 64px, (max-width: 1280px) 6vw, 5vw"
                  />
                  {/* <p className="text-[10px] leading-[165%] xl:text-xs font-semibold text-green-9 text-nowrap">
                    {content.imagesCircle?.image1?.alt}
                  </p> */}
                </div>
                <p
                  className="font-normal leading-[165%] text-justify lg:pr-2.5"
                  dangerouslySetInnerHTML={{ __html: content.description1 }}
                />
              </div>
            )}

            {/* Photos Gallery Tab */}
            {contentKey === "PhotosGallery" && content.images && (
              <div className="grid grid-cols-2 w-full gap-2.5 lg:pr-5 2xl:pr-[12%]">
                {Object.values(content.images)
                  .slice(0, 4)
                  .map((image, index) => (
                    <Image
                      key={`gallery-${index}`}
                      className="w-full h-[180px] 2xl:h-[220px] rounded-md bg-cover"
                      {...image}
                      width={402}
                      height={202}
                      sizes="(max-width: 768px) 100vw, (max-width: 1280px) 70vw, 70vw"
                      alt={`gallery-${index}`}
                    />
                  ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};
export default Tabs;
